'use client';
import React from 'react';
import { useUser, useUserSubscriptions, useDetectIncognitoMode } from '@benzinga/user-context';
import { AuthenticationManager } from '@benzinga/session';
import { TrackingManager } from '@benzinga/tracking-manager';
import { runIdentify } from '@benzinga/analytics';
import type { MetaProps } from '@benzinga/seo';
import { Session } from '@benzinga/session';

interface Props {
  pageProps: { metaProps?: MetaProps };
  session: Session;
}

export const GlobalTracking: React.FC<Props> = ({ pageProps, session }) => {
  const user = useUser();
  const subscriptions = useUserSubscriptions();
  const isIncognito = useDetectIncognitoMode();

  React.useEffect(() => {
    session?.getManager(TrackingManager).setUser(user);
  }, [session, user]);

  React.useEffect(() => {
    session?.getManager(TrackingManager).setSubscriptions(subscriptions);
  }, [session, subscriptions]);

  React.useEffect(() => {
    if (pageProps?.metaProps) {
      session?.getManager(TrackingManager).setMeta(pageProps.metaProps);
    }
  }, [session, pageProps?.metaProps]);

  React.useEffect(() => {
    session?.getManager(TrackingManager).updateContext({ isIncognito: isIncognito });
  }, [session, isIncognito]);

  const lastIdentifiedUserIdRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    const authManager = session.getManager(AuthenticationManager);

    const runTracking = () => {
      const user = authManager.getAuthentication()?.user;
      session.getManager(TrackingManager).setUser(user ?? null);
      if (user && user.accessType !== 'anonymous') {
        if (lastIdentifiedUserIdRef.current !== user.benzingaUid) {
          lastIdentifiedUserIdRef.current = user.benzingaUid;
          runIdentify(session, user);
        }
      } else {
        lastIdentifiedUserIdRef.current = null;
      }
    };

    const authentication = authManager.getAuthentication();

    if (authentication) {
      runTracking();
      return;
    }

    const subscription = authManager.subscribe(
      () => {
        runTracking();
        subscription.unsubscribe();
      },
      undefined,
      ['authentication:session_update'],
    );

    return () => subscription.unsubscribe();
  }, [session]);

  return null;
};
